import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, filter } from 'rxjs';
import { Site } from 'src/app/model/site';
import { AppState } from 'src/app/store/app.reducer';
import * as UserActions from 'src/app/store/user/user.actions';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import { selectIsDraftMode } from 'src/app/store/user/user.selector';
import { selectCurrentSite, selectSiteDeleted } from 'src/app/store/site/site.selector';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import * as SiteActions from 'src/app/store/site/site.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { SiteDialogComponent } from '../components/site-dialog/site.dialog.component';
import { AddGatewayDialogComponent } from '../../shared/components/add-gateway-dialog/add-gateway-dialog.component';
import { EDeviceModel } from 'src/app/conf/device-models';
import { StandardPageLayout } from '../../shared/stdlayoutlib/classes.stdlayoutlib';


@Component({
	selector: 'app-site-button-bar',
	templateUrl: './site-button-bar.component.html',
	providers: [ConfirmationService, DialogService]
})

export class SiteButtonBarComponent implements OnInit, OnDestroy {
	isDraftMode$: Observable<boolean | undefined>;
	isDraftModeSub: Subscription | undefined;
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	deletedSite$: Observable<boolean | undefined>;
	deletedSiteSub: Subscription | undefined;

	site: Site;
	isDraftMode: boolean;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;
	menuitems: MenuItem[] = [];

	// Design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	MIN_MD_WIDTH_EXT = 670;
	MIN_WIDTH_MX7 = 1700;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
		private router: Router,
		private route: ActivatedRoute,
	) {
		this.initSelectors();
	}

	ngOnInit() {
		this.store.dispatch(UserActions.getTechnicians());
		this.store.dispatch(UserFileActions.getSiteImages());
		this.subscribeAll();
		this.onResize();
	}

	initSelectors(): void {
		// Store selectors
		this.site$ = this.store.select(selectCurrentSite);
		this.isDraftMode$ = this.store.select(selectIsDraftMode);
		this.deletedSite$ = this.store.select(selectSiteDeleted);
	}

	subscribeAll(): void {
		this.siteSub = this.site$.pipe(
			filter(cs => !!cs)
		).subscribe((currentSite) => {
			this.site = currentSite;
			this.menuitems = [
				{ label: this.translate.instant('BACK_TO_SITES_LIST'), icon: 'pi cm-arrow-left', command: () => this.toSites(), visible: !this.isDraftMode },
				// { label: this.translate.instant('ADD_DEVICE'), icon: 'pi cm-plus', command: () => this.addGateway(), disabled: this.site.CurrentlyOwned!== undefined && !this.site.CurrentlyOwned},
				{ label: this.translate.instant('EDIT_SITE'), icon: 'pi cm-pencil', command: () => this.editSite(), disabled: this.site.CurrentlyOwned !== undefined && !this.site.CurrentlyOwned },
				//{ label: this.translate.instant('UPDATE_RTC'), icon: 'pi cm-time', command: () => this.updateRtc(), disabled: (this.site.CurrentlyOwned !== undefined && !this.site.CurrentlyOwned) || this.disabledRtc() },
				{ label: this.translate.instant('DELETE_SITE'), icon: 'pi cm-trash', command: () => this.deleteSite() },
			];
		});
		this.isDraftModeSub = this.isDraftMode$.subscribe((isDraftMode) => {
			this.isDraftMode = isDraftMode;
		});
		this.deletedSiteSub = this.deletedSite$.subscribe(deleted => {
			if (deleted) {
				this.store.dispatch(SiteActions.deleteSiteCleanup());
				if (this.isDraftMode) {
					this.exitDraftMode();
				} else {
					this.toSites();
				}
			}
		});
	}

	ngOnDestroy(): void {
		this.siteSub?.unsubscribe();
		this.isDraftModeSub?.unsubscribe();
		this.deletedSiteSub?.unsubscribe();
	}

	toSites() {
		this.router.navigate(['../sites'], { relativeTo: this.route });
	}

	exitDraftMode(): void {
		this.store.dispatch(UserActions.unsetDraftMode());
		this.router.navigate(['/technician/sites-list']);
	}

	disabledRtc(): boolean {
		if (this.site && (this.site.Timezone === undefined || this.site.Timezone === null || this.site.Timezone === "")) {
			return true;
		}
		if (this.site && this.site.Devices && this.site.Devices.length > 0) {
			for (const device of this.site.Devices) {
				if (this.isRbe24230(device.ModelId)) {
					return false;
				}
			}
		}
		return true;
	}

	isRbe24230(deviceModel: number): boolean {
		return deviceModel === EDeviceModel.RBE24230 || deviceModel === EDeviceModel.RBE230 ||
			deviceModel === EDeviceModel.RBE24 || deviceModel === EDeviceModel.GEN_RBE24230;
	}

	createSite(): void {
	}

	editSite(): void {
		this.store.dispatch(SiteActions.resetAddEditSiteForm());
		this.ref = this.dialogService.open(SiteDialogComponent, {
			header: this.translate.instant('EDIT_SITE'),
			width: '85vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				site: this.site
			},
		});
	}

	updateRtc(): void {
		this.store.dispatch(SiteActions.updateAutomationRtc({ siteId: this.site.Id }));
	}

	deleteSite(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.site.CurrentlyOwned ? this.translate.instant('DELETE_SITE') : this.translate.instant('DELETE_SHARED_SITE'),
			message: this.site.CurrentlyOwned ? this.translate.instant('DELETE_SITE_WARNING') : this.translate.instant('DELETE_SHARED_SITE_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				if (this.site.CurrentlyOwned) {
					this.store.dispatch(SiteActions.deleteSite({ id: this.site.Id }));
				} else {
					this.store.dispatch(SiteActions.deleteSharedSite({ id: this.site.Id }));
				}
			},
			reject: () => {
			}
		});
	}

	alertEmptySiteTimezone(): void {
		this.acceptButtonStyle = "p-button-primary"
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('ADD_TIMEZONE_SITE'),
			message: this.translate.instant('ADD_TIMEZONE_SITE_WARNING'),
			acceptLabel: this.translate.instant('CLOSE'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			reject: () => {
			}
		});
	}

	addGateway(): void {
		if (this.site.Timezone === null || this.site.Timezone === undefined || this.site.Timezone === '') {
			this.alertEmptySiteTimezone();
		} else {
			this.ref = this.dialogService.open(AddGatewayDialogComponent, {
				header: this.translate.instant('ADD_DEVICE') + ' / ' + this.translate.instant('ADD_GATEWAY'),
				width: '70vw',
				breakpoints: { '1600px': '98vw' },
				contentStyle: { overflow: 'auto' },
				data: {
					site: this.site
				}
			});
		}
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}
